import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import UserConstants from 'constants/UserConstants';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {TableValidationCellError} from 'notebook/structured-tables/StructuredTableApiConstants';
import excess from 'notebook/tables/excess';
import Preferences from 'Preferences';

import {OutputTableMap} from './excess/Evaluator';
import {CellErrorJSON} from './excess/Util';
import {
  CustomResolutionQuery,
  CustomResolutionQueryType,
  StringResolutionQuery,
} from './string-resolution/StringResolutionConstants';
import {TypedValue} from './TableConstants';

// TODO(BNCH-12478): Finalize shape
export type EvaluationSnapshot = OutputTableMap;

export enum ResolutionQueryType {
  STRING = 'STRING',
  CUSTOM = 'CUSTOM',
}

export interface QueryResolution {
  // Backend should prevent persisting null value, but ensure signed out user can open readonly entry.
  userId: string | null;
  timestamp: string;
  provenanceUuid: string;
  resolvedValue: TypedValue | null;
  error: CellErrorJSON | null;
  query: StringResolutionQuery | CustomResolutionQuery;
}

export interface ValidationErrorSnapshot {
  [tableName: string]: Array<TableValidationCellError>;
}

export type BaseNoteRuntimeSnapshot<TIds> = {
  excessVersion: string | null;
  evaluationSnapshot: EvaluationSnapshot | null;
  validationErrorSnapshot: ValidationErrorSnapshot | null;
  queryResolutions: Array<QueryResolution>;
  languageTag: string;
} & TIds;

export interface NoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<ClientId> {
  isEvaluationPersisted: boolean;
}
export interface FromServerNoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<FromServerId> {}
export interface ToServerNoteRuntimeSnapshot extends BaseNoteRuntimeSnapshot<ToServerId> {
  bootstrapped?: never;
}

export function createNoteRuntimeSnapshot(): NoteRuntimeSnapshot {
  return {
    cid: _.uniqueId('note-runtime-snapshot'),
    // These will be bootstrapped on first evaluation
    excessVersion: null,
    evaluationSnapshot: null,
    isEvaluationPersisted: false,
    validationErrorSnapshot: null,
    queryResolutions: [],
    // Always defined via the browser's navigator global. In cases like in tests fallback to a sensible value.
    languageTag: Preferences.get(UserConstants.LANGUAGE_PREFERENCE_KEY) || 'en-US',
  };
}

export function createNoteRuntimeSnapshotWithEvaluation(
  evaluationSnapshot: OutputTableMap
): NoteRuntimeSnapshot {
  return {
    cid: _.uniqueId('note-runtime-snapshot'),
    excessVersion: excess.EXCESS_VERSION,
    evaluationSnapshot,
    isEvaluationPersisted: false,
    validationErrorSnapshot: null,
    queryResolutions: [],
    // Always defined via the browser's navigator global. In cases like in tests fallback to a sensible value.
    languageTag: Preferences.get(UserConstants.LANGUAGE_PREFERENCE_KEY) || 'en-US',
  };
}

export function prepareNoteRuntimeSnapshotForStore(
  noteRuntimeSnapshot: FromServerNoteRuntimeSnapshot,
  cidIdMap: CidIdMap
  // @ts-expect-error
): asserts noteRuntimeSnapshot is NoteRuntimeSnapshot {
  addCid(noteRuntimeSnapshot, 'note-runtime-snapshot', cidIdMap);
  // @ts-expect-error - we're mutating the object to add this property
  noteRuntimeSnapshot.isEvaluationPersisted = noteRuntimeSnapshot.evaluationSnapshot != null;
}

function onlyLookupQueries(queryResolutions: Array<QueryResolution>): Array<QueryResolution> {
  return queryResolutions.filter(
    (r) =>
      r.query.type === ResolutionQueryType.CUSTOM &&
      r.query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN
  );
}

export function filterLookupColumnQueries(
  queryResolutions: Array<QueryResolution>,
  provenanceUuid: string,
  lookupConfigIds?: Array<string>
): Array<QueryResolution> {
  return queryResolutions.filter(
    (r) =>
      !(
        r.query.type === ResolutionQueryType.CUSTOM &&
        r.query.customResolutionQueryType === CustomResolutionQueryType.LOOKUP_COLUMN &&
        r.provenanceUuid === provenanceUuid &&
        (!lookupConfigIds || lookupConfigIds.includes(r.query.lookupConfigId))
      )
  );
}

function onlyStringResolutionQueries(queryResolutions: Array<QueryResolution>): Array<QueryResolution> {
  return queryResolutions.filter((r) => r.query.type === ResolutionQueryType.STRING);
}

export function prepareNoteRuntimeSnapshotForSave(
  noteRuntimeSnapshot: NoteRuntimeSnapshot,
  cidIdMap: CidIdMap
): ToServerNoteRuntimeSnapshot | null {
  if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_SAVE) {
    return null;
  }
  const {queryResolutions, excessVersion, cid} = noteRuntimeSnapshot;

  if (excessVersion == null) {
    return null;
  }

  const {isEvaluationPersisted, ...noteRuntimeSnapshotForServer} = noteRuntimeSnapshot;

  return {
    ...noteRuntimeSnapshotForServer,
    queryResolutions: onlyLookupQueries(queryResolutions).concat(
      onlyStringResolutionQueries(queryResolutions)
    ),
    id: cidIdMap[cid] as number,
  };
}
